/* Dependencies */
// Services
import { shopifyService } from '../../Services/ShopifyService/ShopifyService.service';

// Modules
import { VariantSelects } from '../VariantSelects/VariantSelects';

// Models
import { ClassElements } from './Product.model';
import { AddToCartRequest } from '../../Services/ShopifyService/ShopifyService.model';
import { productCarousel } from '../ProductCarousel/ProductCarousel';

/**
 * Adds functionality to main product details
 */
class Product {
  private variantSelects: VariantSelects;
  public sectionId: string;
  public originalProductId: string;
  public elements: ClassElements = {
    productContainerElement: null,
    hiddenIdElement: null,
    variantContainerElement: null,
    submitButtonElement: null,
    submitButtonTextElement: null,
    quantityElement: null,
    quantityPlusElement: null,
    quantityMinusElement: null,
    formElement: null,
  };
  public isSubmitting = false;
  public isAvailable = true;

  get rechargeFrequencyElement(): HTMLSelectElement {
    return this.elements.productContainerElement.querySelector(
      '[name="properties[shipping_interval_frequency]'
    );
  }

  get rechargeUnitType(): HTMLInputElement {
    return this.elements.productContainerElement.querySelector(
      '[name="properties[shipping_interval_unit_type]'
    );
  }

  get rechargePurchaseType(): HTMLInputElement {
    return this.elements.productContainerElement.querySelector(
      'input[name=purchase_type]:checked'
    );
  }

  get currentQuantity(): number {
    return parseInt(this.elements.quantityElement.value);
  }

  constructor(productContainer: HTMLElement) {
    this.sectionId = productContainer.id;

    this.elements = {
      ...this.elements,
      productContainerElement: productContainer,
      hiddenIdElement: productContainer.querySelector('[name="id"]'),
    };

    // Retrieve the required elements.
    this.defineElements();

    // Bind the required events.
    this.bindEvents();

    // Set the original prouct id
    this.originalProductId = this.elements.hiddenIdElement.value;

    // Handle variants
    if (this.elements.variantContainerElement) {
      this.variantSelects = new VariantSelects(
        this.elements.variantContainerElement,
        () => {
          this.onVariantChange();
        }
      );
    }
    // Handle settings default state.
    else if (
      this.elements.submitButtonElement.getAttribute('aria-disabled') !== null
    ) {
      this.isAvailable = false;
      this.toggleAddButton(true);
    }
  }

  /**
   * Retrieve the required elements.
   */
  defineElements(): void {
    const { productContainerElement } = this.elements;
    this.elements = {
      ...this.elements,
      variantContainerElement: productContainerElement.querySelector(
        '[data-product-variant]'
      ),
      submitButtonElement: productContainerElement.querySelector(
        '[data-product-submit]'
      ),
      submitButtonTextElement: productContainerElement.querySelector(
        '[data-product-submit-text]'
      ),
      quantityElement: productContainerElement.querySelector('[data-quantity]'),
      quantityPlusElement:
        productContainerElement.querySelector('[data-increment]'),
      quantityMinusElement:
        productContainerElement.querySelector('[data-decrement]'),
      formElement: productContainerElement.querySelector('form'),
    };
  }

  /**
   * Bind the required events
   */
  bindEvents(): void {
    this.elements.quantityPlusElement.addEventListener('click', () => {
      this.updateQuantity(1);
    });

    this.elements.quantityMinusElement.addEventListener('click', () => {
      this.updateQuantity(-1);
    });

    this.elements.formElement.addEventListener('submit', (event) => {
      event.preventDefault();
      this.onSubmitHandler();
    });
  }

  /* ===================================
  Variants
  ====================================*/

  /**
   * Update product info when variant has changed
   */
  onVariantChange(): void {
    this.renderProductInfo();
  }

  /**
   * Fetches product info from API and update product details on page
   */
  renderProductInfo(): void {
    fetch(`?variant=${this.variantSelects.currentVariant.id}`)
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.sectionId}`;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(id);
        const source = html.getElementById(id);

        // Update the variant id.
        this.elements.hiddenIdElement.value =
          this.variantSelects.currentVariant.id;

        // Recharge binds on a click event rather than the change event of the drop
        // downs. Therefore we need to click the containing div to get the price to
        // update.
        this.elements.variantContainerElement.click();

        // Update the destination element.
        if (source && destination) {
          destination.innerHTML = source.innerHTML;
        }

        // Toggle the add button update.
        this.toggleAddButton(!this.variantSelects.currentVariant.available);

        // Update Image
        this.updateMedia();
      });
  }

  /**
   * Controls if add to basket button is enabled
   * @param disable Setting to true disable the add to basket button and chages text to out of stock
   * @returns
   */
  toggleAddButton(disable = true): void {
    const { submitButtonElement, submitButtonTextElement } = this.elements;

    // Ignore if not present.
    if (!submitButtonElement || !submitButtonTextElement) return;

    // Disabled State
    if (disable) {
      this.isAvailable = false;
      submitButtonElement.setAttribute('aria-disabled', 'true');
      submitButtonTextElement.innerHTML = 'Out of Stock';
    } else {
      this.isAvailable = true;
      submitButtonElement.removeAttribute('aria-disabled');
      submitButtonTextElement.innerHTML = 'Add to cart';
    }
  }

  /**
   * Adjusts quantity selected
   * @param qtyAdjustment Amount to change quantity by
   */
  updateQuantity(qtyAdjustment: number): void {
    const { quantityElement } = this.elements;

    // Define the new quantity.
    const newQuantity = this.currentQuantity + qtyAdjustment;

    // Set the new quantity.
    if (newQuantity < 1) {
      quantityElement.value = '1';
    } else {
      quantityElement.value = newQuantity.toString();
    }
  }

  /* ===================================
  Add To Cart
  ====================================*/
  /**
   * Submits product selection to Shopify API
   */
  onSubmitHandler(): void {
    // Ignore if request is processing.
    if (this.isSubmitting || !this.isAvailable) return;

    // Get the required elements.
    const { submitButtonElement, quantityElement } = this.elements;

    // Set the state to processing.
    this.isSubmitting = true;

    // Work out the quantity & form request data.
    let requestData: AddToCartRequest = {
      items: [
        {
          id: this.variantSelects
            ? this.variantSelects.currentVariant.id
            : this.originalProductId,
          quantity: this.currentQuantity,
        },
      ],
    };

    // Handle Recharge
    if (this.rechargeFrequencyElement) {
      requestData.items[0].properties = {
        shipping_interval_frequency: this.rechargeFrequencyElement.value,
        shipping_interval_unit_type: this.rechargeUnitType.value,
      };
    }

    // Add the item to the cart
    shopifyService.addToCart(requestData).then(() => {
      submitButtonElement.removeAttribute('aria-disabled');
      this.isSubmitting = false;
    });
  }

  /* ===================================
  MEDIA
  ====================================*/
  /**
   * Method indended to update the selected image to be of the variant selected
   */
  updateMedia(): void {
    if (
      this.variantSelects.currentVariant &&
      this.variantSelects.currentVariant.featured_media
    ) {
      productCarousel.goToSlideId(
        this.variantSelects.currentVariant.featured_media.id
      );
    }
  }
}

/**
 * Initiates Product Page
 * @returns
 */
export const productInit = () => {
  // Detect if elements are present.
  if (!document.querySelectorAll('[data-product-item]')) return;

  // Get all product elements
  const productElements: HTMLElement[] = Array.from(
    document.querySelectorAll('[data-product-item]')
  );

  // Loop and create product instances.
  productElements.forEach((productContainer) => {
    new Product(productContainer);
  });
};
